// pages/events/App.tsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  Alert,
  Divider,
  Stack,
} from "@mui/material";
import {
  Add,
  PictureAsPdf,
  TableChart,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import {
  useGetEventsQuery,
  useChangeEventStatusMutation,
} from "../../../services/updates.query";
import { DefaultPage } from "../../../layouts/default-page";
import { DataTableFive } from "../../../layouts/layout2/data-table-five";
import { StatusChip } from "../../../components/common/StatusChip";
import { RowActions } from "../../../components/common/RowActions";
import { ExportLoadingBackdrop } from "../../../components/common/ExportLoadingBackdrop";
import { useTableState } from "../../../hooks/useTableState";
import { useNavigate } from "react-router";
import { format } from "date-fns";
import CustomConfirmDialog from "../../../layouts/confirm_dialogue";
import { Notify } from "../../../layouts/notify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const EventsPage: React.FC = () => {
  const navigate = useNavigate();

  const { filters, setFilters, resetFilters } = useTableState({
    search: "",
    page: 1,
  });

  const [searchInput, setSearchInput] = useState(filters.search || "");
  const page = filters.page || 1;
  const pageSize = 100;

  useEffect(() => {
    setSearchInput(filters.search || "");
  }, [filters.search]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; item?: any }>({
    open: false,
  });

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgressText, setExportProgressText] = useState("");

  const {
    data: events,
    isLoading,
    isFetching,
    isError,
  } = useGetEventsQuery("");
  const [changeEventStatus, { isLoading: changing }] =
    useChangeEventStatusMutation();

  const hasActiveFilters = Boolean(filters.search);

  const handleApplySearch = () => {
    setFilters({ search: searchInput.trim(), page: 1 });
  };

  const handleResetFilters = () => {
    setSearchInput("");
    resetFilters();
  };

  const filtered = useMemo(() => {
    const q = (filters.search || "").toLowerCase().trim();
    if (!q) return events || [];
    return (events || []).filter((e: any) =>
      `${e.title || ""}`.toLowerCase().includes(q)
    );
  }, [events, filters.search]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const onEdit = (row: any) => navigate(`edit/${row.id}`);
  const onSoftDelete = (row: any) => setConfirm({ open: true, item: row });

  const handleExportPdf = async () => {
    try {
      setIsExporting(true);
      setExportProgressText("Preparing events PDF...");
      const doc = new jsPDF("landscape");
      doc.setFontSize(16);
      doc.text("MTRCB - Events Report", 14, 15);
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy hh:mm a")} | Total Records: ${filtered.length}`,
        14,
        22
      );

      const tableRows = filtered.map((item: any, index: number) => [
        index + 1,
        item.datetime ? format(new Date(item.datetime), "yyyy-MM-dd") : "—",
        item.title || "—",
        item.is_active ? "ACTIVE" : "INACTIVE",
        item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "—",
      ]);

      autoTable(doc, {
        head: [["#", "Date", "Title", "Status", "Date Created"]],
        body: tableRows,
        startY: 27,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [0, 114, 188] },
      });

      doc.save(`mtrcb-events-${format(new Date(), "yyyyMMdd-HHmm")}.pdf`);
    } catch (err) {
      console.error(err);
      Notify("error", "Failed to export PDF.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      setExportProgressText("Preparing events Excel...");
      const rows = filtered.map((item: any, index: number) => ({
        "No.": index + 1,
        Date: item.datetime ? format(new Date(item.datetime), "yyyy-MM-dd") : "",
        Title: item.title || "",
        Status: item.is_active ? "ACTIVE" : "INACTIVE",
        "Date Created": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Events");
      XLSX.writeFile(
        workbook,
        `mtrcb-events-${format(new Date(), "yyyyMMdd-HHmm")}.xlsx`
      );
    } catch (err) {
      console.error(err);
      Notify("error", "Failed to export Excel.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    {
      id: "rowNumber",
      header: "No.",
      size: 70,
      enableSorting: false,
      Cell: ({ row }: any) => (
        <Typography fontSize={14}>
          {(page - 1) * pageSize + row.index + 1}
        </Typography>
      ),
    },
    {
      accessorKey: "datetime",
      header: "Date",
      size: 150,
      Cell: ({ cell }: any) => (
        <Typography fontSize={14}>
          {cell.getValue() ? format(new Date(cell.getValue()), "MMMM dd, yyyy") : "—"}
        </Typography>
      ),
    },
    {
      accessorKey: "attachments",
      header: "Image",
      size: 100,
      enableSorting: false,
      Cell: ({ cell }: any) => {
        const attachments = cell.getValue();
        const firstImage = attachments?.find((a: any) => a.type === "IMAGE")?.file_url;
        return firstImage ? (
          <img
            src={firstImage}
            alt="Attachment"
            style={{
              width: 60,
              height: 60,
              objectFit: "cover",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={() => {
              setPreviewSrc(firstImage);
              setPreviewOpen(true);
            }}
          />
        ) : (
          <Typography fontSize={13} color="textSecondary">
            No Image
          </Typography>
        );
      },
    },
    {
      accessorKey: "title",
      header: "Title",
      size: 300,
      Cell: ({ cell }: any) => (
        <Typography fontSize={14} fontWeight={600} color="#111827">
          {cell.getValue() || "—"}
        </Typography>
      ),
    },
    {
      accessorKey: "is_active",
      header: "Status",
      size: 130,
      Cell: ({ cell }: any) => {
        const active = !!cell.getValue();
        return (
          <StatusChip
            label={active ? "ACTIVE" : "INACTIVE"}
            tone={active ? "success" : "neutral"}
          />
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 190,
      enableSorting: false,
      Cell: ({ row }: any) => {
        const item = row.original;
        const willDeactivate = !!item.is_active;
        return (
          <RowActions
            actions={[
              {
                label: "Edit",
                onClick: () => onEdit(item),
              },
              {
                label: willDeactivate ? "Deactivate" : "Activate",
                tone: willDeactivate ? "error" : "default",
                onClick: () => onSoftDelete(item),
              },
            ]}
            inline={2}
          />
        );
      },
    },
  ];

  return (
    <DefaultPage
      title="Events"
      subTitle="Manage upcoming and past events."
      primaryButtonProps={{
        startIcon: <Add />,
        children: "Add New Event",
        onClick: () => navigate("add"),
      }}
    >
      <ExportLoadingBackdrop
        open={isExporting}
        message={exportProgressText}
      />

      {/* Standardized Filter Container */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search Events"
            placeholder="Search by title..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleApplySearch();
              }
            }}
            sx={{
              minWidth: { xs: "100%", sm: 320 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "4px",
                fontSize: "13.5px",
              },
            }}
          />
        </Stack>

        <Divider sx={{ my: 2, borderColor: "#F1F5F9" }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Search />}
              onClick={handleApplySearch}
              disabled={isFetching}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: 2,
                backgroundColor: "#0072BC",
                "&:hover": { backgroundColor: "#005a96" },
              }}
            >
              Search
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RestartAlt />}
                onClick={handleResetFilters}
                disabled={isFetching}
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  px: 2,
                  color: "#64748B",
                  borderColor: "#CBD5E1",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPdf}
              disabled={isExporting || filtered.length === 0}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: 2,
                color: "#334155",
                borderColor: "#CBD5E1",
                "&:hover": {
                  borderColor: "#94A3B8",
                  backgroundColor: "#F8FAFC",
                },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<TableChart />}
              onClick={handleExportExcel}
              disabled={isExporting || filtered.length === 0}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: 2,
                backgroundColor: "#0072BC",
                "&:hover": { backgroundColor: "#005a96" },
              }}
            >
              Export Excel
            </Button>
          </Box>
        </Box>
      </Box>

      {isError && (
        <Box mb={2}>
          <Alert severity="error">
            Failed to load events.
          </Alert>
        </Box>
      )}

      <Box mb={2}>
        <Alert severity="success" sx={{ borderRadius: "6px" }}>
          Total matched records: <strong>{Number(filtered.length || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={paginatedData}
        total={filtered.length}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage) => setFilters({ page: newPage })}
        columns={columns as any}
        getRowId={(row: { id: any }) => String(row.id)}
        isLoading={isLoading}
        isFetching={isFetching}
        onRowClick={(row: any) => onEdit(row.original)}
      />

      {/* Image preview */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: "6px",
          }}
        >
          {previewSrc && (
            <img
              src={previewSrc}
              alt="Full"
              style={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: "4px" }}
            />
          )}
        </Box>
      </Modal>

      {/* Confirm soft delete/restore */}
      <CustomConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={async () => {
          if (!confirm.item) return;
          const target = confirm.item;
          try {
            await changeEventStatus({
              id: target.id,
              is_active: !target.is_active,
            }).unwrap();
            Notify(
              "success",
              target.is_active ? "Event deactivated." : "Event restored."
            );
          } catch (e: any) {
            Notify("error", e?.message || "Failed to change status.");
          } finally {
            setConfirm({ open: false });
          }
        }}
        loading={changing}
        title={confirm.item?.is_active ? "Deactivate Event?" : "Restore Event?"}
        description={
          confirm.item?.is_active
            ? "This will hide the event (soft delete). You can restore it anytime."
            : "This will make the event visible again."
        }
        color={confirm.item?.is_active ? "error" : "success"}
      />
    </DefaultPage>
  );
};

export default EventsPage;
